/* eslint-disable */
import * as React from 'react'
import OutlinedInput from '@mui/material/OutlinedInput'
import './KeyTypeDropdown.css'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import { TextField, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import DropDownImage from '../assets/dropDown.svg'
import Modal from './Modal'
import LanguageStrings from '../i18n/locales'

const KeyTypeMappings = LanguageStrings().HSMConfiguration.KeyTypeMappings

export interface Option {
  value: string
  label: string
}

interface KeyTypeDropdownProps {
  options: Option[]
  value: Option | null
  onSelect: (selectedValue: Option) => void
  onCreateNew: (name: string) => void
  placeholder?: string
  width?: string | number
  label?: string
  disabled?: boolean
  dataTestId?: string
  menuStyle?: React.CSSProperties
  menuItemStyle?: React.CSSProperties
}

const injectLabel = (label: string | undefined): JSX.Element => {
  return <span style={{ fontSize: '15px', paddingBottom: 10 }}>{label}</span>
}

export const KeyTypeDropdown: React.FC<KeyTypeDropdownProps> = ({
  options,
  value,
  onSelect,
  onCreateNew,
  placeholder,
  width,
  label,
  disabled,
  dataTestId,
  menuStyle,
  menuItemStyle
}) => {
  const MENU_HEIGHT = 150
  const DEFAULT_DROPDOWN_WIDTH = 260
  const [search, setSearch] = React.useState('')
  const [filteredOptions, setFilteredOptions] = React.useState<Option[]>(options)
  const [openAddModal, setOpenAddModal] = React.useState(false)
  const [newKeyTypeName, setNewKeyTypeName] = React.useState('')
  const anchorRef = React.useRef<HTMLDivElement | null>(null)
  const selectRef = React.useRef<HTMLDivElement | null>(null)
  const outlinedRef = React.useRef<HTMLDivElement | null>(null)
  const computeWidth = (): number => (outlinedRef.current?.offsetWidth ?? anchorRef.current?.offsetWidth ?? defaultMenuWidth)
  const defaultMenuWidth = React.useMemo(() => (typeof width === 'number' ? width : DEFAULT_DROPDOWN_WIDTH as number), [width])
  React.useLayoutEffect(() => {
    const el = outlinedRef.current ?? anchorRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const w = computeWidth()
      if (w > 0) setMenuWidth(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [defaultMenuWidth])

  const [menuWidth, setMenuWidth] = React.useState<number>(0)
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false)
  const [anchorPos, setAnchorPos] = React.useState<{ top: number, left: number }>({ top: 0, left: 0 })
  React.useEffect(() => {
    setFilteredOptions(options.filter((option) => option.label?.toLowerCase().includes(search.toLowerCase())))
  }, [options, search])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value)
  }

  const handleReset = (event: SelectChangeEvent<string>): void => {
    setSearch('')
    setFilteredOptions(options)
    setMenuOpen(false)
    const selectedValue = event.target.value as string
    const selectedLabel = options.find(opt => opt.value === selectedValue)?.label ?? selectedValue
    onSelect({ value: selectedValue, label: selectedLabel })
  }

  const handleClose = (): void => {
    setSearch('')
    setFilteredOptions(options)
    setMenuOpen(false)
  }

  const handleModalKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      const name = newKeyTypeName.trim()
      if (name.length > 0) {
        onCreateNew(name)
        onSelect({ value: name, label: name })
        setOpenAddModal(false)
        setNewKeyTypeName('')
      }
    }
  }

  const getVisualViewport = () => (window as any).visualViewport

  const getAnchorPosition = (): { top: number, left: number } => {
    const el = outlinedRef.current ?? anchorRef.current
    if (!el) return { top: 0, left: 0 }
    const rect = el.getBoundingClientRect()
    const vv = getVisualViewport()
    const offsetTop = vv?.offsetTop ?? 0
    const offsetLeft = vv?.offsetLeft ?? 0
    const viewportHeight = vv?.height ?? window.innerHeight
    const dpr = window.devicePixelRatio || 1
    const spaceBelow = (viewportHeight - rect.bottom)
    const placeAbove = spaceBelow < MENU_HEIGHT && rect.top > spaceBelow
    const desiredTop = placeAbove ? (rect.top - MENU_HEIGHT) : rect.bottom
    const preciseTop = (desiredTop + window.scrollY + offsetTop) * dpr
    const targetWidth = (menuWidth || defaultMenuWidth)
    const preciseLeft = (rect.left + window.scrollX + offsetLeft + (rect.width - targetWidth) / 2) * dpr
    const top = Math.round(preciseTop) / dpr
    const left = Math.round(preciseLeft) / dpr
    return { top, left }
  }

  const unlockScroll = (): void => {
    try {
      const b = document.body
      const html = document.documentElement
      b.style.overflow = 'auto'
      b.style.overflowY = 'auto'
      html.style.overflow = 'auto'
      html.style.overflowY = 'auto'
      b.classList.remove('mui-fixed')
    } catch {}
  }

  React.useEffect(() => {
    if (!menuOpen) return
    const update = (): void => {
      setMenuWidth(computeWidth())
      setAnchorPos(getAnchorPosition())
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    const vv = getVisualViewport()
    if (vv) {
      vv.addEventListener('resize', update)
      vv.addEventListener('scroll', update)
    }
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      if (vv) {
        vv.removeEventListener('resize', update)
        vv.removeEventListener('scroll', update)
      }
    }
  }, [menuOpen])

  const textFieldSx = {
    root: {
      backgroundColor: '#404040',
      width: '100%',
      '& .MuiInputBase-input': {
        color: '#F0F0F0',
        height: '30px',
        paddingLeft: '10px',
        borderRadius: '0px'
      },
      '& .MuiInputBase-input::placeholder': {
        color: '#92979C'
      },
      '& .MuiInputLabel-root': {
        color: '#F0F0F0',
        boxShadow: 'none',
        '&.Mui-focused': {
          color: '#F0F0F0'
        }
      },
      '& .MuiInput-underline:before': { borderBottomColor: '#00000000' },
      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#00000000' },
      '& .MuiInput-underline:after': { borderBottomColor: '#00000000' },
      '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: '#6F6F6F !important' },
        '&:hover fieldset': { borderColor: '#6F6F6F !important' },
        '&.Mui-focused fieldset': { borderColor: '#6F6F6F !important' }
      }
    }
  }

  const renderCreateNew = (): JSX.Element => (
    <MenuItem
      data-testid={`${dataTestId ?? 'keytype-dropdown'}-create-new`}
      value={'+__create_new__'}
      onClick={(e) => {
        e.stopPropagation()
        setNewKeyTypeName('')
        setOpenAddModal(true)
      }}
      sx={{
        color: '#64C3FF !important',
        backgroundColor: '#303030',
        '&:hover': { backgroundColor: '#2a2a2a', color: '#64C3FF !important' },
        height: '40px !important',
        padding: '8px 8px !important',
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        overflowX: 'hidden',
        position: 'sticky',
        left: 0,
        zIndex: 2
      }}
    >
      {KeyTypeMappings.AddCreateNewKeyType}
    </MenuItem>
  )


  return (
    <div className='keyTypeDropdownDiv'>
      <FormControl>
        {label !== undefined && label !== '' && injectLabel(label)}
        <Box
          ref={anchorRef}
          className='keytype-dropdown-container'
          sx={{
            width: width !== undefined ? (typeof width === 'number' ? `${width}px` : width) : DEFAULT_DROPDOWN_WIDTH,
            border: 0,
            borderRadius: '4px',
            backgroundColor: '#272727',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'visible',
            boxShadow: 'inset 0 0 0 1px #404040',
            '&:hover': { boxShadow: 'inset 0 0 0 1px #404040' },
            '&:focus-within': { boxShadow: 'inset 0 0 0 2px #64C3FF' }
          }}
        >
          <Select
            ref={selectRef}
            className='keytype-dropdown'
            required
            onKeyDownCapture={(event) => { event.stopPropagation() }}
            onClick={() => { if (menuOpen) handleClose() }}
            value={value?.value ?? ''}
            displayEmpty
            data-testid={dataTestId}
            label={dataTestId}
            onChange={handleReset}
            IconComponent={() => <img src={DropDownImage} alt='dropdown' width={20} height={20} color='#ffffff' style={{ margin: '4px 4px 0 0', cursor: 'pointer', pointerEvents: 'none', position: 'absolute', right: 0 }}/>}
            onClose={handleClose}
            onOpen={() => { setSearch(''); setFilteredOptions(options); setMenuWidth(computeWidth()); setMenuOpen(true); setAnchorPos(getAnchorPosition()); unlockScroll() }}
            aria-label={dataTestId}
            MenuProps={{
              style: menuStyle,
              sx: { pointerEvents: 'none' },
              anchorReference: 'anchorPosition',
              anchorPosition: anchorPos,
              transformOrigin: { vertical: 0, horizontal: 0 },
              disablePortal: false,
              container: document.body,
              ModalProps: { disableScrollLock: true, hideBackdrop: false, disableEnforceFocus: true, disableRestoreFocus: true, onBackdropClick: () => handleClose() },
              slotProps: { backdrop: { style: { pointerEvents: 'auto' } }, root: { disableScrollLock: true } } as any,
              disableScrollLock: true,
              keepMounted: true,
              transitionDuration: 0,
              disableAutoFocusItem: true,
              marginThreshold: 0,
              variant: 'menu',
              PaperProps: {
                sx: {
                  pointerEvents: 'auto',
                  boxSizing: 'border-box',
                  mt: 0,
                  width: defaultMenuWidth,
                  height: 150,
                  position: 'fixed',
                  transform: 'none',
                  backgroundColor: '#404040',
                  boxShadow: '0 0 10px #404040',
                  zIndex: 9999,
                  maxHeight: 150,
                   overflowY: 'hidden',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#6F6F6F #2a2a2a',
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#2a2a2a' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#6F6F6F', borderRadius: '4px' },
                  '& .MuiMenuItem-root': {
                    color: '#f0f0f0',
                    padding: '5px 8px',
                    '&:hover': { backgroundColor: '#303030' }
                  },
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: '#2f4858'
                  },
                  '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: '#2f4858'
                  }
                }
              },
              MenuListProps: {
                sx: {
                  width: '100%',
                  backgroundColor: '#404040',
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  boxSizing: 'border-box',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#6F6F6F #2a2a2a',
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#2a2a2a' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#6F6F6F', borderRadius: '4px' }
                }
              }
            } as any}
            renderValue={(selectedValue) => (
              options.find((option) => option.value === selectedValue)?.label || (placeholder !== undefined ? placeholder : 'Select an option')
            )}
            input={
              <OutlinedInput
                ref={outlinedRef}
                id={value?.value ?? ''}
                notched={false}
                sx={{
                  backgroundColor: '#272727',
                  borderRadius: '4px',
                  border: '0 !important',
                  boxShadow: 'none !important',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s ease, border-width 0.15s ease',
                  width: '100%',
                  height: '40px',
                  overflow: 'hidden',
                  '& fieldset': {
                    borderColor: 'transparent !important',
                    borderWidth: '0px',
                    borderStyle: 'solid'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent !important',
                    borderWidth: '0px'
                  },
                  '&:hover': { border: '0 !important', boxShadow: 'none !important' },
                  '&:hover fieldset': { borderColor: 'transparent !important' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent !important' },
                  '&.Mui-focused': { border: '0 !important', boxShadow: 'none !important' },
                  '&.Mui-focused fieldset': { borderColor: 'transparent !important', borderWidth: '0px' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent !important', borderWidth: '0px' },
                  '& .MuiSelect-select': {
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '8px',
                    paddingRight: '32px'
                  },
                  '& .MuiSelect-select:focus': { outline: 'none' }
                }}
              />
            }
            disabled={disabled ?? false}
            sx={{
              background: '#00000000',
              width: width !== undefined ? width : `${DEFAULT_DROPDOWN_WIDTH}px`,
              height: '40px',
              boxShadow: 'none',
              color: 'rgba(240, 240, 240, 1)', fontFamily: 'Honeywell Sans Web', fontSize: '14px',
              borderRadius: '4px',
              padding: '0 !important',
              outline: 'none !important',
              '& .MuiOutlinedInput-root': { backgroundColor: '#404040', borderRadius: '4px', overflow: 'hidden' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent !important' },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent !important' },
              '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'transparent !important' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent !important' },
            }}
            disableUnderline
          >
            {/* Search input with icon */}
            <TextField
              key={`search-${menuOpen}`}
              variant="standard"
              id='keytype-search'
              placeholder={KeyTypeMappings.Search}
              value={search}
              onChange={handleSearch}
              size='small'
              className='custom-textfield'
              sx={{ ...textFieldSx.root, position: 'sticky', left: 0, zIndex: 2, overflowX: 'hidden' }}
              autoComplete='off'
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start" sx={{ pl: '6px' }}>
                    <SearchIcon style={{ color: '#F0F0F0' }} />
                  </InputAdornment>
                )
              }}
            />
            {/* Create new action */}
            {renderCreateNew()}
            {/* Options list */}
            {filteredOptions.length > 0 ? (
              (
                filteredOptions.map((option, index) => (
                  <MenuItem
                    sx={{
                      color: '#f0f0f0',
                      padding: '5px 8px',
                      '&:hover': { backgroundColor: '#303030' },
                      width: '100%',
                      ...menuItemStyle
                    }}
                    data-testid={`${dataTestId}-${index}`}
                    key={index}
                    value={option.value}
                  >
                    <span style={{ display: 'inline-block', minWidth: 'max-content', whiteSpace: 'nowrap' }}>
                      {option.label}
                    </span>
                  </MenuItem>
                ))
              )
            ) : (
              <MenuItem data-testid={'no-options'}>{'No options'}</MenuItem>
            )}
          </Select>
        </Box>
      </FormControl>
      {/* Add Key Type Modal */}
      {openAddModal && (
        <Modal
          title={KeyTypeMappings.AddKeyType}
          onClose={() => { setOpenAddModal(false); setNewKeyTypeName('') }}
          LabelNegative={KeyTypeMappings.Cancel}
          LabelPositive={KeyTypeMappings.Add}
          buttonTypeNegative={'hollow'}
          buttonTypePositive={'filled'}
          positiveDisabled={newKeyTypeName.trim().length === 0}
          onClickNegative={() => { setOpenAddModal(false); setNewKeyTypeName('') }}
          onClickPositive={() => {
            const name = newKeyTypeName.trim()
            if (name.length > 0) {
              onCreateNew(name)
              onSelect({ value: name, label: name })
              setOpenAddModal(false)
              setNewKeyTypeName('')
            }
          }}
        >
          <div style={{ paddingTop: '20px' }}>
            <div style={{ color: '#F0F0F0', marginBottom: '12px', fontSize: '14px', fontWeight: 500 }}>{KeyTypeMappings.Name}</div>
            <TextField
              fullWidth
              placeholder={''}
              value={newKeyTypeName}
              onChange={(e) => setNewKeyTypeName(e.target.value)}
              onKeyDown={handleModalKeyDown}
              variant="outlined"
              size="small"
              autoComplete="off"
              autoFocus
              name="new-key-type-name"
              inputProps={{ autoComplete: 'off', autoCorrect: 'off' }}
              sx={{
                backgroundColor: '#272727',
                '& .MuiInputBase-input': {
                  color: '#F0F0F0',
                  backgroundColor: '#404040',
                  padding: '6px 10px',
                  height: '32px'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#404040',
                  borderRadius: '8px'
                },
                '& .MuiOutlinedInput-root:hover': { backgroundColor: '#404040' },
                '& .MuiOutlinedInput-root.Mui-focused': { backgroundColor: '#404040' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00000000' },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00000000' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00000000' }
              }}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}